import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from "../firebase";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar";

export function FeedbackDisplay() {
  const [feedbacks, setFeedbacks] = useState([]);

  useEffect(() => {
    const fetchFeedbacks = async () => {
      const snap = await getDocs(collection(db, 'feedbacks'));
      const data = snap.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(f => f.rating); // Only show rated feedbacks
      setFeedbacks(data);
    };
    fetchFeedbacks();
  }, []);

  return (
    <div className="min-h-screen bg-[#020617] text-slate-100 selection:bg-blue-500/10 overflow-x-hidden font-inter">
      <Navbar />

      {/* Background Graphics */}
      <div className="fixed inset-0 bg-grid opacity-10 pointer-events-none" />
      <div className="fixed top-0 right-0 w-[800px] h-[800px] bg-blue-500/5 blur-[120px] rounded-full -mr-48 -mt-48 animate-blob" />
      <div className="fixed bottom-0 left-0 w-[600px] h-[600px] bg-indigo-500/5 blur-[120px] rounded-full -ml-32 -mb-32 animate-blob delay-2000" />
      <div className="fixed inset-0 bg-mesh opacity-20 pointer-events-none" />

      <div className="container mx-auto max-w-7xl relative z-10 py-64 px-6 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="relative mb-32"
        >
          <div className="absolute -inset-14 bg-blue-500/10 blur-[130px] rounded-full pointer-events-none" />
          <h1 className="text-7xl md:text-[10rem] font-black text-white mb-10 tracking-[0.02em] leading-[0.85] relative z-10 uppercase font-inter">
            System <br /><span className="text-gradient from-blue-600 via-slate-600 to-indigo-600">Integrity.</span>
          </h1>
          <p className="text-xl md:text-3xl text-slate-400 max-w-4xl mx-auto mb-20 leading-relaxed font-medium lowercase tracking-tight relative z-10">
            real-time authentication of enterprise satisfaction across global operations.
            precision-engineered verification cycles.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 text-left">
          {feedbacks.map((f, idx) => (
            <motion.div
              key={f.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1, duration: 0.6 }}
              whileHover={{ rotateY: 5, rotateX: -2, scale: 1.02 }}
              style={{ perspective: 1000 }}
              className="group glass-card p-14 rounded-[4.5rem] border border-white/80 transition-all duration-700 hover:shadow-2xl hover:bg-slate-900/40/95 glow-border flex flex-col justify-between"
            >
              {/* Profile Header */}
              <div className="flex items-center gap-6 mb-12">
                <div className="relative">
                  <div className="absolute -inset-2 bg-blue-500/20 blur-xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
                  <img
                    src={f.photoURL || 'https://via.placeholder.com/50'}
                    alt={f.clientName}
                    className="w-20 h-20 rounded-3xl object-cover border border-white/5 relative z-10 group-hover:scale-110 group-hover:rotate-6 transition-all duration-700 shadow-sm"
                  />
                </div>
                <div className="space-y-1">
                  <h3 className="text-lg font-black text-white uppercase tracking-tighter leading-none">
                    {f.viewingName || f.clientName || 'Anonymous ID'}
                  </h3>
                  <p className="text-[10px] font-black text-blue-600 uppercase tracking-[0.3em] leading-none pt-1">
                    {f.projectName || 'Classified Build'}
                  </p>
                </div>
              </div>

              {/* Integrity Rating */}
              <div className="flex gap-2 mb-10">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className={`h-2 rounded-full transition-all duration-700 ${i < f.rating ? 'w-6 bg-blue-600 shadow-[0_0_10px_rgba(37,99,235,0.4)]' : 'w-2 bg-slate-100'}`} />
                ))}
              </div>

              {/* Narrative */}
              <p className="text-slate-400 text-sm leading-relaxed mb-12 italic font-medium relative pl-8 border-l-2 border-white/5 lowercase tracking-tight">
                "{f.comment}"
              </p>

              {/* Visual Assets */}
              {f.images?.length > 0 && (
                <div className="grid grid-cols-2 gap-5 mb-12">
                  {f.images.slice(0, 2).map((img, i) => (
                    <div key={i} className="relative overflow-hidden rounded-[2.5rem] border border-slate-50 h-36">
                      <img
                        src={img}
                        alt="visual-asset"
                        className="w-full h-full object-cover transition-all duration-700 cursor-crosshair group-hover:scale-110"
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Metadata Footer */}
              <div className="pt-10 border-t border-white/5 flex justify-between items-center mt-auto">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse shadow-[0_0_10px_rgba(34,197,94,0.4)]" />
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Verification: Active</span>
                </div>
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">
                  {f.date || 'Syncing...'}
                </span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          className="mt-48 pt-32 border-t border-white/5 max-w-4xl mx-auto"
        >
          <h2 className="text-4xl font-black text-white mb-10 uppercase tracking-tighter leading-none">Become Part of <br /><span className="text-gradient">Immensity.</span></h2>
          <p className="text-slate-400 text-lg font-medium lowercase tracking-tight mb-12">we are architecting the future for enterprises that demand perfection.</p>
          <button
            onClick={() => window.location.href = '/contact'}
            className="px-16 py-7 bg-blue-600 text-white font-black rounded-[2.5rem] hover:bg-blue-700 transition-all hover:scale-105 active:scale-95 uppercase tracking-widest text-[11px] shadow-[0_20px_40px_rgba(37,99,235,0.3)]"
          >
            INITIATE SPRINT
          </button>
        </motion.div>
      </div>

      <footer className="py-24 px-6 bg-[#020617] border-t border-white/5 text-center relative z-20">
        <p className="text-[10px] font-black text-slate-600 uppercase tracking-[0.5em]">© 2024 LAB Developers — Quality Assurance Protocol.</p>
      </footer>
    </div>
  );
}
